import React, { useEffect, useRef, useState } from "react";
import TigerLogo from "@/components/TigerLogo";
import { api } from "@/lib/api";

const DATE_KEY = "tiger:splashDate";
const VERSION_KEY = "tiger:splashVersion";
const MIN_MS = 300;

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

export default function SplashScreen() {
  const [visible, setVisible] = useState(() => {
    try { return localStorage.getItem(DATE_KEY) !== todayStr(); }
    catch (err) { console.warn("[splash] localStorage read failed", err); return false; }
  });
  const [leaving, setLeaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const startedAt = useRef(Date.now());

  // Track backend/config check so an admin "reset splash" can force re-appearance
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/config");
        const remoteVer = String(data?.splash_reset_version ?? 0);
        const localVer = (() => {
          try { return localStorage.getItem(VERSION_KEY) || "0"; }
          catch (err) { console.warn("[splash] version read failed", err); return "0"; }
        })();
        if (remoteVer !== localVer) {
          if (!cancelled) setVisible(true);
          try { localStorage.setItem(VERSION_KEY, remoteVer); }
          catch (err) { console.warn("[splash] version write failed", err); }
        }
      } catch (err) {
        console.info("[splash] config check skipped (offline)", err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Real progress: DOM ready, fonts, network sample, window load
  useEffect(() => {
    if (!visible) return;
    startedAt.current = Date.now();
    try { localStorage.setItem(DATE_KEY, todayStr()); }
    catch (err) { console.warn("[splash] date write failed", err); }

    let p = 10;
    const bump = (to) => { p = Math.max(p, to); setProgress(p); };

    bump(20); // component mounted → DOM ready

    // Fonts
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => bump(55));
    } else {
      setTimeout(() => bump(55), 200);
    }
    // Backend ping
    api.get("/info").then(() => bump(80)).catch(() => bump(65));
    // window.load
    if (document.readyState === "complete") bump(100);
    else window.addEventListener("load", () => bump(100), { once: true });

    // Fallback drip so it never sticks
    const drip = setInterval(() => setProgress((cur) => Math.min(100, cur + 3)), 120);
    return () => clearInterval(drip);
  }, [visible]);

  // When ready AND min duration met, fade out
  useEffect(() => {
    if (!visible) return;
    if (progress < 100) return;
    const elapsed = Date.now() - startedAt.current;
    const wait = Math.max(0, MIN_MS - elapsed);
    const t1 = setTimeout(() => setLeaving(true), wait);
    const t2 = setTimeout(() => setVisible(false), wait + 250);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [progress, visible]);

  const skip = () => {
    setLeaving(true);
    setTimeout(() => setVisible(false), 180);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="splash-screen"
      onClick={skip}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === " " || e.key === "Enter" || e.key === "Escape") skip(); }}
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-300 cursor-pointer ${
        leaving ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
      style={{ background: "linear-gradient(135deg, #f97316 0%, #f59e0b 50%, #fbbf24 100%)" }}
    >
      <div className="flex flex-col items-center gap-6" style={{ animation: "tigerSplashIn 700ms cubic-bezier(0.22, 1, 0.36, 1) both" }}>
        <div
          className="rounded-3xl shadow-2xl shadow-orange-900/40"
          style={{ animation: "tigerSplashPulse 700ms ease-in-out both" }}
        >
          <TigerLogo size={140} />
        </div>
        <div className="text-white text-center">
          <div className="font-heading text-3xl font-semibold tracking-tight">@Tiger Professional</div>
          <div className="text-xs uppercase tracking-[0.4em] opacity-90 mt-2">Modulo Formativo · v.23.0</div>
        </div>

        <div className="w-64 mt-2" data-testid="splash-progress">
          <div className="h-1.5 rounded-full bg-white/25 overflow-hidden">
            <div
              className="h-full bg-white transition-[width] duration-200 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-2 text-[10px] uppercase tracking-widest text-white/80">
            <span>Caricamento…</span>
            <span className="font-mono">{progress}%</span>
          </div>
        </div>

        <div className="text-white/60 text-[10px] uppercase tracking-widest mt-1">Tocca per saltare</div>
      </div>

      <style>{`
        @keyframes tigerSplashIn {
          0%   { opacity: 0; transform: translateY(12px) scale(0.94); }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes tigerSplashPulse {
          0%   { transform: scale(0.85); filter: drop-shadow(0 0 0 rgba(255,255,255,0)); }
          50%  { transform: scale(1.06); filter: drop-shadow(0 0 24px rgba(255,255,255,0.6)); }
          100% { transform: scale(1);    filter: drop-shadow(0 0 0 rgba(255,255,255,0)); }
        }
      `}</style>
    </div>
  );
}
